import type { FunnelAddToCartItem } from './types';

export interface SemanticPortResult {
  /** Bounded, non-PII values safe to correlate with the result observation. */
  resultParams?: Record<string, unknown>;
  /**
   * The dispatcher normally owns the result envelope. Cross-document actions
   * may defer it to the destination after that host establishes success.
   */
  resultOwner?: 'dispatcher' | 'destination';
  /** True when the capability initiated navigation and later actions must stop. */
  navigated?: boolean;
}

export interface SemanticPortContext {
  signal: AbortSignal;
  stepId: string;
  funnelId?: string;
  blockId?: string;
  eventId?: string;
  correlationId?: string;
}

export interface CaptureLeadInput {
  formId?: string;
  leadSource?: string;
  /** Raw form value is delivered only to the trusted host capability. */
  value?: unknown;
}

export interface AddToCartInput {
  productId: string;
  variantId: string;
  quantity: number;
  item?: FunnelAddToCartItem;
}

export interface StorefrontSemanticPorts {
  captureLead?: (
    input: CaptureLeadInput,
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void>;
  revealCoupon?: (
    input: { couponCode?: string; targetBlockId?: string },
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void> | SemanticPortResult | void;
  runSpin?: (
    input: { wheelId?: string; targetBlockId?: string },
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void> | SemanticPortResult | void;
  acceptOffer?: (
    input: { offerId?: string; stepId?: string },
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void>;
  declineOffer?: (
    input: { offerId?: string; stepId?: string },
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void>;
  dismissExperience?: (
    input: { experienceId?: string; targetBlockId?: string },
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void> | SemanticPortResult | void;
  addToCart?: (
    input: AddToCartInput,
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void>;
  startCheckout?: (
    input: { cartId?: string; mode?: 'cart' | 'buy_now' },
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void>;
  selectVariant?: (
    input: {
      productId?: string;
      variantId: string;
      targetBlockId?: string;
    },
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void> | SemanticPortResult | void;
  applyCoupon?: (
    input: { couponCode: string },
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void>;
  removeFromCart?: (
    input: { productId?: string; variantId?: string; lineId?: string },
    context: SemanticPortContext,
  ) => Promise<SemanticPortResult | void>;
}

export function semanticPortContext(
  context: Pick<
    import('./types').ActionExecutionContext,
    'signal' | 'stepId' | 'intentEnvelope'
  >,
): SemanticPortContext {
  return {
    signal: context.signal,
    stepId: context.stepId,
    funnelId: context.intentEnvelope?.payload.context.funnelId,
    blockId: context.intentEnvelope?.payload.context.blockId,
    eventId: context.intentEnvelope?.metadata.eventId,
    correlationId: context.intentEnvelope?.metadata.correlationId,
  };
}
